import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Edit2, Building2, Mail, Phone, KeyRound } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { listarClientes, criarCliente, atualizarCliente, desativarCliente, excluirClientePermanente, Cliente } from "../services/clientes";
import { listarProjetos, arquivarProjeto } from "../services/projetos";
import { useConfirm } from "../components/ConfirmProvider";
import { SearchInput, matchBusca } from "../components/SearchInput";
import { UsuariosClienteModal } from "./clientes/UsuariosClienteModal";
import { ClienteFormModal } from "./clientes/ClienteFormModal";

export function Clientes() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Cliente | null>(null);
  const [usuariosCliente, setUsuariosCliente] = useState<Cliente | null>(null);
  const [busca, setBusca] = useState("");
  const [mostrarInativos, setMostrarInativos] = useState(false);
  // Filtro por modalidade dos projetos ATIVOS do cliente (PDF — Thainá).
  const [filtroModalidade, setFiltroModalidade] = useState<"todos" | "mensal" | "avulso">("todos");

  const { data: clientes = [], isLoading } = useQuery({
    queryKey: ["clientes"],
    queryFn: listarClientes,
    enabled: can(["gestor", "admin"]),
  });

  // Projetos externos ativos — usados pra saber quais clientes têm contrato
  // mensal e/ou avulso em andamento.
  const { data: projetosData } = useQuery({
    // A chave começa com "projetos" DE PROPÓSITO: assim ela é invalidada junto
    // com as outras queries de projeto (o React Query casa por prefixo). Como
    // "projetos-modalidade-clientes" era uma string só, criar ou editar um
    // projeto NÃO atualizava a contagem Mensais/Avulsos desta tela.
    queryKey: ["projetos", "modalidade-clientes"],
    queryFn: () => listarProjetos({ interno: false, arquivados: false, perPage: 500 }),
    enabled: can(["gestor", "admin"]),
  });
  const clientesComMensal = new Set<string>();
  const clientesComAvulso = new Set<string>();
  for (const p of (projetosData?.projetos ?? []) as any[]) {
    if (!p.clienteId) continue;
    if (p.modalidade === "mensal") clientesComMensal.add(p.clienteId);
    else clientesComAvulso.add(p.clienteId);
  }

  const porModalidade =
    filtroModalidade === "todos"
      ? clientes
      : clientes.filter((c) =>
          filtroModalidade === "mensal" ? clientesComMensal.has(c.id) : clientesComAvulso.has(c.id),
        );

  const CAMPOS_BUSCA = [
    (c: Cliente) => c.codigo, (c: Cliente) => c.nome, (c: Cliente) => c.email,
    (c: Cliente) => c.documento, (c: Cliente) => c.telefone,
  ];
  /** Aplica busca + "mostrar inativos" — o recorte comum da lista e das contagens. */
  const aplicarFiltrosBase = (lista: Cliente[]) =>
    matchBusca(mostrarInativos ? lista : lista.filter((c) => c.ativo), busca, CAMPOS_BUSCA);

  const filtrados = aplicarFiltrosBase(porModalidade);

  // Quantos clientes caem em cada aba (pedido da Thainá, ago/2026: "poderíamos
  // colocar algo para conseguir visualizar essa quantidade, geral, mensal e
  // avulso"). Usa os MESMOS filtros da lista, então o número sempre bate com o
  // que aparece ao clicar — em vez de ela clicar e contar na mão.
  const contagem = {
    todos: aplicarFiltrosBase(clientes).length,
    mensal: aplicarFiltrosBase(clientes.filter((c) => clientesComMensal.has(c.id))).length,
    avulso: aplicarFiltrosBase(clientes.filter((c) => clientesComAvulso.has(c.id))).length,
  };

  function fmtHoras(h: number | undefined | null) {
    if (h == null) return "—";
    const horas = Math.floor(h);
    const minutos = Math.round((h - horas) * 60);
    return `${String(horas).padStart(2, "0")}h${String(minutos).padStart(2, "0")}`;
  }

  const criarM = useMutation({
    mutationFn: criarCliente,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clientes"] }); setShowForm(false); },
  });

  const updateM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) => atualizarCliente(id, dados),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clientes"] }); setEditing(null); setShowForm(false); },
  });

  const desativarM = useMutation({
    mutationFn: desativarCliente,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clientes"] }),
  });

  // Inativar o cliente NÃO mexe nos projetos dele (só o `arquivado` tira projeto
  // de circulação). Sem isso o cliente sai do portal e das previsões, mas os
  // projetos seguem na lista, no quadro e contando como atrasados — meio termo
  // que confunde. Aqui a gente oferece arquivar junto, na mesma ação.
  const arquivarProjetosM = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await arquivarProjeto(id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["clientes"] });
      // Pega a lista de projetos E a contagem Mensais/Avulsos (mesmo prefixo).
      qc.invalidateQueries({ queryKey: ["projetos"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  // Projetos em andamento de cada cliente (a query de projetos acima já traz só
  // externos e não arquivados).
  const projetosAtivosPorCliente = new Map<string, { id: string; nome: string }[]>();
  for (const p of (projetosData?.projetos ?? []) as any[]) {
    if (!p.clienteId) continue;
    const lista = projetosAtivosPorCliente.get(p.clienteId) ?? [];
    lista.push({ id: p.id, nome: p.nome });
    projetosAtivosPorCliente.set(p.clienteId, lista);
  }

  async function inativarCliente(c: Cliente) {
    const projetos = projetosAtivosPorCliente.get(c.id) ?? [];
    const ok = await confirm({
      title: "Inativar cliente",
      message: `Inativar ${c.nome}?\n\nOs dados ficam preservados e nada é apagado. O cliente sai das contagens (como a previsão de peças do mês) e perde o acesso ao portal.`,
      danger: true,
      confirmText: "Inativar",
    });
    if (!ok) return;
    await desativarM.mutateAsync(c.id);

    // Segunda pergunta só quando há o que arquivar — quem não tem projeto aberto
    // não precisa levar um diálogo extra na cara.
    if (projetos.length === 0) return;
    const nomes = projetos.slice(0, 6).map((p) => `• ${p.nome}`).join("\n");
    const resto = projetos.length > 6 ? `\n…e mais ${projetos.length - 6}` : "";
    const ok2 = await confirm({
      title: "Arquivar os projetos dele também?",
      message:
        `${c.nome} tem ${projetos.length} ${projetos.length === 1 ? "projeto em andamento" : "projetos em andamento"}:\n\n${nomes}${resto}\n\n` +
        `Inativar o cliente NÃO tira os projetos de circulação: eles continuam na lista de Projetos, com as tarefas no quadro, e podem aparecer como atrasados.\n\n` +
        `Arquivar tira tudo de circulação sem apagar nada — dá para restaurar em Projetos → "Exibir arquivados".`,
      confirmText: `Arquivar ${projetos.length === 1 ? "o projeto" : `os ${projetos.length} projetos`}`,
      cancelText: "Deixar como estão",
    });
    if (ok2) await arquivarProjetosM.mutateAsync(projetos.map((p) => p.id));
  }

  const excluirM = useMutation({
    mutationFn: ({ id, forcar }: { id: string; forcar: boolean }) => excluirClientePermanente(id, forcar),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["clientes"] }),
  });

  async function handleExcluirPermanente(c: Cliente) {
    const ok = await confirm({
      title: "⚠️ Excluir permanentemente",
      message: (
        <>
          <p><strong>{c.nome}</strong> será removido <strong className="text-red-600">para sempre</strong>. Esta ação não pode ser desfeita.</p>
        </>
      ),
      danger: true,
      confirmText: "Excluir",
    });
    if (!ok) return;
    try {
      await excluirClientePermanente(c.id, false);
      qc.invalidateQueries({ queryKey: ["clientes"] });
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409) {
        const dets = e?.response?.data?.error?.detalhes || {};
        const ok2 = await confirm({
          title: "Cliente tem dados vinculados",
          message: (
            <>
              <p>Apagar mesmo assim irá remover:</p>
              <ul className="text-xs text-slate-600 list-disc ml-5 mt-1 space-y-0.5">
                {dets.projetos > 0 && <li>{dets.projetos} projeto(s) (com tarefas, contratos etc.)</li>}
                {dets.usuarios > 0 && <li>{dets.usuarios} usuário(s) do portal vinculado(s)</li>}
              </ul>
              <p className="text-red-600 text-xs mt-2 font-semibold">Esta ação é irreversível.</p>
            </>
          ),
          danger: true,
          confirmText: "Apagar TUDO mesmo assim",
        });
        if (ok2) excluirM.mutate({ id: c.id, forcar: true });
      } else {
        alert(e?.response?.data?.error?.message || "Erro ao excluir");
      }
    }
  }

  if (!can(["gestor", "admin"])) {
    return <div className="text-slate-500">Acesso restrito.</div>;
  }
  const podeCriar = can(["gestor", "admin"]);
  const podeExcluirPermanente = can(["admin"]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Clientes</h1>
          <p className="text-slate-500 mt-1">Cadastro de clientes e seus acessos ao portal.</p>
          {/* Resumo por modalidade. A soma mensal+avulso NÃO fecha com o total de
              propósito — cliente pode ter os dois tipos de projeto, e quem não
              tem projeto em andamento não entra em nenhum dos dois. Dizer isso
              aqui evita a conta na mão que não bate. */}
          <p className="text-slate-400 text-xs mt-1">
            <strong className="text-slate-600">{contagem.todos}</strong>{" "}
            {contagem.todos === 1 ? "cliente" : "clientes"} ·{" "}
            <strong className="text-slate-600">{contagem.mensal}</strong> com contrato mensal ·{" "}
            <strong className="text-slate-600">{contagem.avulso}</strong> com projeto avulso
            <span className="block mt-0.5">
              Um cliente pode ter os dois, e quem não tem projeto em andamento aparece só em “Todos” —
              por isso a soma não fecha com o total.
            </span>
          </p>
        </div>
        {podeCriar && (
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
            <Plus size={16} /> Novo cliente
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <SearchInput value={busca} onChange={setBusca} placeholder="Buscar por código, nome, e-mail ou documento…" className="flex-1 min-w-[220px]" />
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-sm">
          {([
            { id: "todos", label: "Todos" },
            { id: "mensal", label: "Mensais" },
            { id: "avulso", label: "Avulsos" },
          ] as const).map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setFiltroModalidade(opt.id)}
              title={
                opt.id === "todos"
                  ? "Todos os clientes cadastrados"
                  : `Clientes com pelo menos um projeto ${opt.id === "mensal" ? "mensal" : "avulso"} em andamento`
              }
              className={
                "px-3 py-1.5 font-medium transition-colors flex items-center gap-1.5 " +
                (filtroModalidade === opt.id ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50")
              }
            >
              {opt.label}
              <span
                className={
                  "text-[11px] tabular-nums px-1.5 py-0.5 rounded-full " +
                  (filtroModalidade === opt.id ? "bg-blue-500/60 text-white" : "bg-slate-100 text-slate-500")
                }
              >
                {contagem[opt.id]}
              </span>
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1.5 text-sm text-slate-600 px-3">
          <input type="checkbox" checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)} />
          Mostrar inativos
        </label>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-3 text-left font-medium text-slate-500">Código</th>
              <th className="px-3 py-3 text-left font-medium text-slate-500">Nome</th>
              <th className="px-3 py-3 text-left font-medium text-slate-500">Contato</th>
              <th className="px-3 py-3 text-center font-medium text-slate-500">Projetos</th>
              <th className="px-3 py-3 text-right font-medium text-slate-500" title="Horas estimadas (Σ esforço das tarefas)">Totais</th>
              <th className="px-3 py-3 text-right font-medium text-slate-500" title="Horas úteis já trabalhadas">Investidas</th>
              <th className="px-3 py-3 text-right font-medium text-slate-500" title="Totais − Investidas">Restantes</th>
              <th className="px-3 py-3 text-center font-medium text-slate-500">Acessos</th>
              <th className="px-3 py-3 text-center font-medium text-slate-500">Status</th>
              <th className="px-3 py-3 text-right font-medium text-slate-500">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">Carregando…</td></tr>}
            {!isLoading && filtrados.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">{busca ? "Nenhum cliente encontrado" : "Nenhum cliente cadastrado"}</td></tr>}
            {filtrados.map((c) => (
              <tr key={c.id} className="hover:bg-slate-50 transition-colors">
                <td className="px-3 py-3 font-mono text-xs text-slate-600 whitespace-nowrap">{c.codigo || <span className="text-slate-400 italic">—</span>}</td>
                <td className="px-3 py-3 font-medium text-slate-800">
                  <Link to={`/clientes/${c.id}`} className="inline-flex items-center gap-2 hover:text-blue-600 hover:underline">
                    <Building2 size={14} className="text-slate-400 shrink-0" />
                    {c.nome}
                  </Link>
                </td>
                <td className="px-3 py-3 text-slate-600 text-xs">
                  {c.email && <div className="flex items-center gap-1"><Mail size={10} /> {c.email}</div>}
                  {c.telefone && <div className="flex items-center gap-1"><Phone size={10} /> {c.telefone}</div>}
                </td>
                <td className="px-3 py-3 text-center text-slate-600">{c._count?.projetos ?? 0}</td>
                <td className="px-3 py-3 text-right text-slate-700 font-mono text-xs">{fmtHoras(c.horasTotais)}</td>
                <td className="px-3 py-3 text-right text-slate-700 font-mono text-xs">{fmtHoras(c.horasInvestidas)}</td>
                <td className="px-3 py-3 text-right font-mono text-xs">
                  <span className={c.horasRestantes != null && c.horasRestantes <= 0 ? "text-red-600 font-semibold" : "text-emerald-700"}>
                    {fmtHoras(c.horasRestantes)}
                  </span>
                </td>
                <td className="px-3 py-3 text-center">
                  <button
                    onClick={() => setUsuariosCliente(c)}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-slate-100 hover:bg-blue-100 hover:text-blue-700 text-slate-700 transition-colors"
                    title="Gerenciar acessos do cliente"
                  >
                    <KeyRound size={11} /> {c._count?.usuarios ?? 0}
                  </button>
                </td>
                {/* O selo de status é o próprio botão de ativar/inativar (pedido
                    da Thainá, ago/2026 — ela não achou o ícone de desativar que
                    ficava escondido entre as ações, e REATIVAR só existia dentro
                    da tela de edição). Mesma ideia da troca rápida de status do
                    projeto. */}
                <td className="px-3 py-3 text-center">
                  {podeCriar ? (
                    <button
                      type="button"
                      disabled={desativarM.isPending || updateM.isPending}
                      onClick={async () => {
                        if (c.ativo) {
                          await inativarCliente(c);
                        } else {
                          const ok = await confirm({
                            title: "Reativar cliente",
                            message: `Reativar ${c.nome}?\n\nEle volta a contar nos relatórios. Dois pontos: o acesso das pessoas dele ao portal continua desligado (religue em "Acessos"), e projetos que tiverem sido arquivados continuam arquivados — restaure em Projetos → "Exibir arquivados".`,
                            confirmText: "Reativar",
                          });
                          if (ok) updateM.mutate({ id: c.id, dados: { ativo: true } });
                        }
                      }}
                      title={c.ativo ? "Cliente ativo — clique para inativar" : "Cliente inativo — clique para reativar"}
                      className={`px-2 py-0.5 rounded text-xs font-medium transition-colors disabled:opacity-50 ${
                        c.ativo
                          ? "bg-green-100 text-green-700 hover:bg-amber-100 hover:text-amber-700"
                          : "bg-red-100 text-red-700 hover:bg-green-100 hover:text-green-700"
                      }`}
                    >
                      {c.ativo ? "Ativo" : "Inativo"}
                    </button>
                  ) : (
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.ativo ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                      {c.ativo ? "Ativo" : "Inativo"}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 text-right space-x-2 whitespace-nowrap">
                  {podeCriar && (
                    <>
                      <button onClick={() => { setEditing(c); setShowForm(true); }} className="text-blue-600 hover:text-blue-700" title="Editar">
                        <Edit2 size={14} />
                      </button>
                      {/* O antigo ícone de desativar saiu daqui: virou o próprio
                          selo da coluna Status, que também REATIVA (o ícone só
                          desativava, e ninguém achava). */}
                      {podeExcluirPermanente && (
                        <button
                          onClick={() => handleExcluirPermanente(c)}
                          className="text-slate-400 hover:text-red-600"
                          title="Excluir permanentemente"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && podeCriar && (
        <ClienteFormModal
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSubmit={(dados) => {
            if (editing) updateM.mutate({ id: editing.id, dados });
            else criarM.mutate(dados);
          }}
          loading={criarM.isPending || updateM.isPending}
          error={(criarM.error as any)?.response?.data?.error?.message || (updateM.error as any)?.response?.data?.error?.message}
        />
      )}

      {usuariosCliente && (
        <UsuariosClienteModal
          clienteId={usuariosCliente.id}
          clienteNome={usuariosCliente.nome}
          onClose={() => setUsuariosCliente(null)}
        />
      )}
    </div>
  );
}

